// src/auditoria/components/ds/DsFilterPill.tsx
//
// Implementa a classe do canvas `.filtpill` — 53 declarações, instanciada em
// 36 artboards (DESIGN-SYSTEM.md §4.3).
//
// `display:inline-flex;align-items:center;gap:6px;background:var(--bg-neutral);
//  border:1px solid var(--divider);border-radius:999px;font-size:11px;
//  font-weight:700;color:var(--text-secondary);padding:5px 6px 5px 12px;
//  white-space:nowrap`. O padding assimétrico é proposital: a folga menor à
// direita é o berço do X/chevron.
// Variante B (`ordem-servico`, `usuarios` kit B): `padding:4px 6px 4px 10px` → `dense`.
// X literal do canvas: `<span style="display:inline-grid;place-items:center;width:18px;
//  height:18px;border-radius:50%;background:var(--action-selected);font-size:10px">×</span>`.
//
// Substitui o `<TextField select size="small" label="…">` de ~40px com label
// flutuante (indexadores, motivo, malha) e o `<Chip size="small">` de 13px sem
// borda — ambos leem como campo de formulário, não como filtro já aplicado.
//
// ----------------------------------------------------------------------
// RODADA DE CONSERTO DA CAMADA COMPARTILHADA — DECISOES-PENDENTES.md §3.11
// Pedido por 4 LOTES (L12, L22, L23, L25); L08 registra o caso vizinho.
//
// Três defeitos conferidos no arquivo antes de mexer:
//   1. `onClick?: () => void` não recebia o evento.
//   2. Não havia `forwardRef`, então a pílula não servia de âncora de `<Menu>`.
//   3. `hasMenu` só desenhava o chevron — não abria nada.
// O resultado: os quatro lotes escreveram o MESMO embrulho (`<Box component="span"
// ref>` em volta da pílula, `useState` do anchor, `<Menu>` irmão), duplicado dentro
// de cada território, em telas que somam 36 artboards com `.filtpill`.
//
// O que mudou:
//   - `onClick` e `onDelete` passaram a receber o evento. ADITIVO: todo `() => void`
//     que já existe continua atribuível.
//   - O componente é `forwardRef` — quem quiser continuar controlando o próprio
//     `<Menu>` por fora agora ancora direto na pílula, sem `<span>` extra.
//   - Prop nova `menu`: com ela a pílula ABRE o menu sozinha (guarda a âncora, o
//     estado de aberto e fecha no clique de dentro). `hasMenu` continua existindo e
//     continua significando só "desenhe o chevron", para não quebrar chamada antiga;
//     passar `menu` implica o chevron.
// O `<Menu>` sai vestido com o `.menu` do canvas: `background:var(--bg-paper);
// border:1px solid var(--divider);border-radius:8px;box-shadow:var(--shadow-dropdown);
// overflow:hidden;min-width:210px` (Biblioteca-dropdown-aberto). O `--shadow-dropdown`
// do canvas é, valor por valor, o `customShadows.dropdown` do tema.

import type { Ref, ReactNode, MouseEvent, KeyboardEvent } from 'react';
import type { Theme, CSSObject } from '@mui/material/styles';

import { useRef, useState, forwardRef, useCallback } from 'react';

import Menu from '@mui/material/Menu';
import { styled } from '@mui/material/styles';

import { DsIcon } from './DsIcon';
import { DS_BOX_RADIUS, DS_PILL_RADIUS } from './tokens';

// ----------------------------------------------------------------------

export type DsFilterPillProps = {
  children: ReactNode;
  /** Desenha o X do canvas. Recebe o evento — o clique já vem com `stopPropagation`. */
  onDelete?: (evento: MouseEvent<HTMLButtonElement>) => void;
  onClick?: (evento: MouseEvent<HTMLElement>) => void;
  /** Troca o X por chevron (a pílula abre um menu em vez de limpar o filtro). */
  hasMenu?: boolean;
  /**
   * Conteúdo do menu (normalmente `<MenuItem>`s). Com ele a pílula vira a âncora e
   * gerencia abrir/fechar sozinha; o chevron aparece sem precisar de `hasMenu`.
   */
  menu?: ReactNode;
  /** Chamado depois que o menu fecha — útil para devolver o foco ou limpar rascunho. */
  onMenuClose?: () => void;
  /** Variante `ordem-servico`: padding 4px 6px 4px 10px. */
  dense?: boolean;
};

type EstiloPilula = { dense: boolean; clicavel: boolean };

const anelDeFoco = (theme: Theme): CSSObject => ({
  '&:focus-visible': {
    outline: `2px solid ${theme.vars.palette.primary.main}`,
    outlineOffset: 2,
  },
});

const estiloPilula = (theme: Theme, { dense, clicavel }: EstiloPilula): CSSObject => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  background: theme.vars.palette.background.neutral,
  border: `1px solid ${theme.vars.palette.divider}`,
  borderRadius: DS_PILL_RADIUS,
  fontSize: 11,
  fontWeight: 700,
  fontFamily: 'inherit',
  lineHeight: 1.5,
  color: theme.vars.palette.text.secondary,
  padding: dense ? '4px 6px 4px 10px' : '5px 6px 5px 12px',
  whiteSpace: 'nowrap',
  ...(clicavel && { cursor: 'pointer', appearance: 'none' }),
  ...anelDeFoco(theme),
});

const naoRepassar = (prop: PropertyKey) => prop !== 'dense' && prop !== 'clicavel';

const PilulaSpan = styled('span', { shouldForwardProp: naoRepassar })<EstiloPilula>(
  ({ theme, dense, clicavel }) => estiloPilula(theme, { dense, clicavel })
);

const PilulaBotao = styled('button', { shouldForwardProp: naoRepassar })<EstiloPilula>(
  ({ theme, dense, clicavel }) => estiloPilula(theme, { dense, clicavel })
);

/** O X do canvas: círculo de 18px sobre `action.selected`, glifo `×` de 10px. */
const BotaoLimpar = styled('button')(({ theme }) => ({
  display: 'grid',
  placeItems: 'center',
  width: 18,
  height: 18,
  flex: '0 0 auto',
  padding: 0,
  border: 0,
  borderRadius: '50%',
  background: theme.vars.palette.action.selected,
  color: 'inherit',
  fontFamily: 'inherit',
  fontSize: 10,
  lineHeight: 1,
  cursor: 'pointer',
  appearance: 'none',
  ...anelDeFoco(theme),
}));

// ----------------------------------------------------------------------

function DsFilterPillRaiz(
  {
    children,
    onDelete,
    onClick,
    hasMenu = false,
    menu,
    onMenuClose,
    dense = false,
  }: DsFilterPillProps,
  ref: Ref<HTMLElement>
) {
  const ancora = useRef<HTMLElement | null>(null);
  const [menuAberto, setMenuAberto] = useState(false);

  // Uma referência só serve a dois donos: a âncora interna do menu e o `ref` de quem
  // chamou (que continua recebendo o nó da pílula, não um `<span>` embrulhando).
  const guardarNo = useCallback(
    (no: HTMLElement | null) => {
      ancora.current = no;

      if (typeof ref === 'function') {
        ref(no);
      } else if (ref) {
        ref.current = no;
      }
    },
    [ref]
  );

  const temMenuProprio = Boolean(menu);
  const mostrarChevron = hasMenu || temMenuProprio;
  const clicavel = Boolean(onClick) || temMenuProprio;

  const fecharMenu = useCallback(() => {
    setMenuAberto(false);
    onMenuClose?.();
  }, [onMenuClose]);

  const aoClicar = (evento: MouseEvent<HTMLElement>) => {
    onClick?.(evento);

    if (temMenuProprio && !evento.defaultPrevented) {
      setMenuAberto(true);
    }
  };

  // Enter/Espaço dispara um clique de verdade no nó, em vez de inventar um evento de
  // mouse a partir do evento de teclado. Assim `onClick` recebe sempre um `MouseEvent`
  // legítimo, com `currentTarget` e `stopPropagation` funcionando.
  const aoTeclar = (evento: KeyboardEvent<HTMLSpanElement>) => {
    if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      ancora.current?.click();
    }
  };

  const conteudo = (
    <>
      {children}

      {onDelete && (
        <BotaoLimpar
          type="button"
          aria-label="Remover filtro"
          onClick={(evento) => {
            evento.stopPropagation();
            onDelete(evento);
          }}
        >
          ×
        </BotaoLimpar>
      )}

      {!onDelete && mostrarChevron && (
        <DsIcon name="chevron-baixo" size={18} sx={{ flex: '0 0 auto' }} />
      )}
    </>
  );

  // O `.menu` do canvas. O `slotProps.list` zera o padding vertical porque o `.mi` do
  // canvas já traz `padding:10px 14px` e a lista não tem folga extra em cima/embaixo.
  const painelDoMenu = temMenuProprio && (
    <Menu
      anchorEl={ancora.current}
      open={menuAberto}
      onClose={fecharMenu}
      onClick={fecharMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        list: { sx: { paddingY: 0 } },
        paper: {
          sx: (theme) => ({
            marginTop: '4px',
            minWidth: 210,
            overflow: 'hidden',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: `${DS_BOX_RADIUS}px`,
            boxShadow: theme.vars.customShadows.dropdown,
            '& .MuiMenuItem-root': {
              gap: '10px',
              paddingX: '14px',
              paddingY: '10px',
              fontSize: 13,
            },
          }),
        },
      }}
    >
      {menu}
    </Menu>
  );

  // O X é sempre um `button` de verdade. Por isso a raiz só vira `button` quando
  // não há X — botão dentro de botão é marcação inválida. Com os dois, a raiz
  // continua `span` e ganha papel e teclado.
  if (clicavel && !onDelete) {
    return (
      <>
        <PilulaBotao
          ref={guardarNo}
          type="button"
          onClick={aoClicar}
          dense={dense}
          clicavel
          {...(temMenuProprio && { 'aria-haspopup': 'menu' as const, 'aria-expanded': menuAberto })}
        >
          {conteudo}
        </PilulaBotao>
        {painelDoMenu}
      </>
    );
  }

  return (
    <>
      <PilulaSpan
        ref={guardarNo}
        dense={dense}
        clicavel={clicavel}
        {...(clicavel && {
          role: 'button',
          tabIndex: 0,
          onClick: aoClicar,
          onKeyDown: aoTeclar,
        })}
        {...(temMenuProprio && { 'aria-haspopup': 'menu' as const, 'aria-expanded': menuAberto })}
      >
        {conteudo}
      </PilulaSpan>
      {painelDoMenu}
    </>
  );
}

export const DsFilterPill = forwardRef(DsFilterPillRaiz);

DsFilterPill.displayName = 'DsFilterPill';
